import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { AiAttachmentInfo } from '@euler/model/apdm';
import { FileSpreadsheet, Image as ImageIcon, X } from 'lucide-react-native';
import { memo } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { aiColors } from '../theme';

export function formatFileSize(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * A sheet or image the user attached, in the composer or a sent message.
 * `onOpen` makes it a button that opens the file; `opening` shows it busy.
 */
export const AttachmentChip = memo(
  ({
    info,
    onRemove,
    onOpen,
    opening,
  }: {
    info: Pick<AiAttachmentInfo, 'fileName' | 'mimeType' | 'sizeBytes'>;
    onRemove?: () => void;
    onOpen?: () => void;
    opening?: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    const image = info.mimeType.startsWith('image/');
    const Icon = image ? ImageIcon : FileSpreadsheet;
    return (
      <Pressable
        accessibilityRole={onOpen ? 'button' : undefined}
        accessibilityLabel={onOpen ? `打开 ${info.fileName}` : undefined}
        disabled={!onOpen || opening}
        onPress={onOpen}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(10),
          padding: rpx(8),
          borderRadius: rpx(12),
          borderWidth: 1,
          borderColor: aiColors.border,
          backgroundColor: aiColors.card,
          maxWidth: '100%',
        }}
      >
        <View
          style={{
            width: rpx(34),
            height: rpx(34),
            borderRadius: rpx(9),
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: aiColors.accent,
          }}
        >
          {opening ? (
            <ActivityIndicator size="small" color={aiColors.accentForeground} />
          ) : (
            <Icon size={18} color={aiColors.accentForeground} />
          )}
        </View>
        <View style={{ flexShrink: 1 }}>
          <Typography.Text
            medium
            size={rpx(12)}
            color={aiColors.foreground}
            numberOfLines={1}
          >
            {info.fileName}
          </Typography.Text>
          <Typography.Text size={rpx(10)} color={aiColors.mutedForeground}>
            {formatFileSize(info.sizeBytes)}
          </Typography.Text>
        </View>
        {onRemove && (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`移除 ${info.fileName}`}
            onPress={onRemove}
            hitSlop={8}
            // At the chip's end when the composer stretches it.
            style={{ padding: rpx(4), marginLeft: 'auto' }}
          >
            <X size={16} color={aiColors.mutedForeground} />
          </Pressable>
        )}
      </Pressable>
    );
  },
);
